import Quill from 'quill';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import VariableDropdown from '../VariableDropdown';
import store from '../../../../../store/store';
import { BrowserRouter, Routes, Route } from 'react-router-dom';

const Module = Quill.import('core/module');

class VariableDropdownModule extends Module {
  constructor(quill, options) {
    super(quill, options);

    this.storyVariables = options.storyVariables;
    this.botId = options.botId;
    this.includedVariableTypes = options.includedVariableTypes;
    this.searchTerm = '';
    this.isDropdownVisible = false;

    this.dropdown = document.createElement('div');
    this.dropdown.style.display = 'none';

    this.quill.on('text-change', this.onTextChange.bind(this));
    this.quill.on('selection-change', this.onSelectionChange.bind(this));

    document.addEventListener('click', this.onClickOutside.bind(this));

    // Add a keyboard binding for ArrowDown in RTE to intercept navigation
    this.quill.keyboard.addBinding(
      { key: 40 /* ArrowDown */ },
      this.handleArrowDown.bind(this)
    );

    // Listen for Escape in RTE to override parent listener and dismiss dropdown first
    this.captureEscFn = this.handleEscapeCapture.bind(this);
    quill.root.addEventListener('keydown', this.captureEscFn, true);

    document.body.appendChild(this.dropdown);
  }

  destroy() {
    this.quill.root.removeEventListener('keydown', this.captureEscFn, true);
  }

  // Handle Escape when dropdown has no focus yet and the internal handler has not kicked in
  handleEscapeCapture(e) {
    if (e.key === 'Escape' || e.keyCode === 27) {
      if (this.isDropdownVisible) {
        e.preventDefault();
        e.stopPropagation();
        this.hideDropdown();
      }
    }
  }

  handleArrowDown(range, context) {
    // If the dropdown is visible, transfer focus to it
    if (this.isDropdownVisible && this.dropdown) {
      // Use the focusable <div> in VariableDropdown with tabIndex="0" prop
      const focusableDropdown = this.dropdown.querySelector('[tabindex="0"]');
      if (focusableDropdown) {
        focusableDropdown.focus();
      }
      // Return false so Quill doesn't also move the cursor
      return false;
    }
    // Otherwise, return handling to Quill
    return true;
  }

  onTextChange(delta, oldDelta, source) {
    const cursorPosition = this.quill.getSelection()?.index;

    if (!cursorPosition) {
      this.hideDropdown();
      return;
    } else {
      const textBeforeCursor = this.quill.getText(0, cursorPosition);

      const currentOpenBrackets = textBeforeCursor.lastIndexOf('{{');

      if(currentOpenBrackets !== -1) {
        const textAfterBrackets = textBeforeCursor.slice(currentOpenBrackets + 2);

        const detectedClosingBrackets = textAfterBrackets.lastIndexOf('}');

        if(detectedClosingBrackets !== -1) {
          this.hideDropdown();
        } else {
          this.searchTerm = textAfterBrackets?.trim();
          this.showDropdown();
        }
      } else {
        this.hideDropdown();
      }
    }
  }

  onSelectionChange(range, oldRange, source) {
    if (range && oldRange && source === 'user') {
      this.hideDropdown();
    }
  }

  showDropdown() {
    if (this.isDropdownVisible) {
      // If already visible, just update the search term
      this.updateDropdown();
      return;
    }
    // Workaround for LiveChatSystemMessagesEditor positioning of variables dropdown
    const lcSystemsTable = this.quill.container.closest('.ant-table-wrapper');
    const newParent = lcSystemsTable || document.body;
    // Only re-append if the parent is different
    if (this.dropdown.parentNode !== newParent) {
      if (this.dropdown.parentNode) {
        this.dropdown.parentNode.removeChild(this.dropdown);
      }
      newParent.appendChild(this.dropdown);
    }

    this.isDropdownVisible = true;

    this.dropdown.innerHTML = '';

    setTimeout(() => {
      this.renderDropdown();
    }, 0);

    this.dropdown.style.position = 'absolute';
    this.dropdown.style.top = '20px';
    this.dropdown.style.zIndex = 10000;

    const cursorPosition = this.quill.getSelection()?.index;
    const { left: cursorLeft, top: cursorTop } = this.quill.getBounds(cursorPosition);
    const editorElement = this.quill.container;
    const editorBounds = editorElement.getBoundingClientRect();
    let dropdownLeft;
    let dropdownTop;
    let containerWidth;

    if (lcSystemsTable) { // Positioning in case of lc messages (parent element is ant-table-wrapper)
      const tableBounds = lcSystemsTable.getBoundingClientRect();
      const offsetLeft = editorBounds.left - tableBounds.left;
      const offsetTop = editorBounds.top - tableBounds.top;
      dropdownLeft = offsetLeft + cursorLeft;
      dropdownTop = offsetTop + cursorTop + 20;
      containerWidth = tableBounds.width;
    } else {  // Generic positioning (parent element is document)
      const editorTop = editorBounds.top + window.pageYOffset;
      const editorLeft = editorBounds.left + window.pageXOffset;
      dropdownLeft = editorLeft + cursorLeft;
      dropdownTop = editorTop + cursorTop + 20;
      containerWidth = window.innerWidth;
    }

    // If the space on the right is not enough, move the dropdown left
    if (dropdownLeft + editorBounds.width > containerWidth) {
      dropdownLeft = dropdownLeft < 0 ? 0 : containerWidth - editorBounds.width; // Prevent negative left if table is narrower than editor width
    }

    this.dropdown.style.left = `${dropdownLeft}px`;
    this.dropdown.style.top = `${dropdownTop}px`;
    this.dropdown.style.display = 'block';
  }

  hideDropdown() {
    if (!this.isDropdownVisible) {
      return;
    }

    this.isDropdownVisible = false;
    this.searchTerm = '';

    ReactDOM.unmountComponentAtNode(this.dropdown);
    this.dropdown.style.display = 'none';
  }

  updateDropdown() {
    this.renderDropdown();
  }

  renderDropdown() {
    ReactDOM.render(
      <Provider store={store}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/org/:orgId/*"
              element={
                <VariableDropdown
                  storyVariables={this.storyVariables}
                  rteEditor={this.quill}
                  botId={this.botId}
                  suffix="}}"
                  onAfterAdd={() => this.hideDropdown()}
                  onHideDropdown={() => this.hideDropdown()}
                  includedVariableTypes={this.includedVariableTypes}
                  searchTerm={this.searchTerm}
                  isHeadlessSearch={true}
                />
              }
            />
          </Routes>
        </BrowserRouter>
      </Provider>,
      this.dropdown
    );
  }

  onClickOutside(event) {
    if (!this.dropdown.contains(event.target)) {
      this.hideDropdown();
    }
  }
}

Quill.register('modules/variableDropdown', VariableDropdownModule, true);
export default VariableDropdownModule;
